import React, { useEffect, useState } from "react";
import { Navigate } from "react-router";
import { useDispatch } from "react-redux";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  DragOverlay,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import Button from "@shared/v2/Button";
import { Popover, PopoverItem } from "@shared/v2/Popover";
import TextComboInput from "@shared/v2/TextComboInput";
import TextLink from "@shared/v2/TextLink";
import {
  ArrowUpRightFromSquareSolidV6,
  ChevronDownSolidV6,
  MagnifyingGlassSolidV6,
  PlusSolidV6,
} from "@shared/v2/Icomoon";
import WebsiteHeader from "../shared/WebsiteHeader";
import AboutWebsiteProfilesModal from "./AboutWebsiteProfilesModal";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import { useFeatureFlags } from "../../reducers/layoutReducer/selectors";
import AgentLenderCard, { SortableAgentLenderCard } from "./AgentLenderCard";
import PreferredLenderModal from "./PreferredLenderModal";
import { getSeatsAvailable } from "../../reducers/websiteReducer/websiteReducer";
import PersonDetailsModal from "../../Contacts/PersonDetailsModal";
import { useSeatsAvailable } from "../../reducers/websiteReducer/selectors";
import NoSeatsRemainingModal from "./NoSeatsRemainingModal";
import DeleteLenderModal from "./DeleteLenderModal";
import DeleteAgentModal from "./DeleteAgentModal";

const ADD_OPTIONS = [
  {
    label: "New Website Profile",
    value: "website_profile",
    meta: { description: "Add someone to your website without granting access to the CRM" },
  },
  {
    label: "New CRM User",
    value: "crm_user",
    meta: { description: "Invite a new agent to your CRM and display them on the website" },
  },
];

const SORT_OPTIONS = [
  { label: "First Name (A → Z)", value: "first_name", meta: { dir: "asc" } },
  { label: "First Name (Z → A)", value: "first_name", meta: { dir: "desc" } },
  { label: "Last Name (A → Z)", value: "last_name", meta: { dir: "asc" } },
  { label: "Last Name (Z → A)", value: "last_name", meta: { dir: "desc" } },
  { label: "Date Added (Asc.)", value: "state", meta: { dir: "asc" } },
  { label: "Date Added (Desc.)", value: "state", meta: { dir: "desc" } },
  { label: "Random", value: "random" },
  { label: "Custom", value: "manual" },
];

const initialFeatured = [
  {
    id: "a1",
    firstName: "One",
    lastName: "Agent",
    title: "Test Test",
    isFeatured: true,
    showOnWebsiteRoster: true,
    showAgentProfilePage: true,
  },
  {
    id: "a2",
    firstName: "Two",
    lastName: "Agent",
    title: "Test Test",
    isFeatured: true,
    showOnWebsiteRoster: true,
    showAgentProfilePage: true,
  },
  {
    id: "a3",
    firstName: "Three",
    lastName: "Agent",
    title: "Test Test",
    isFeatured: true,
    showOnWebsiteRoster: true,
    showAgentProfilePage: true,
  },
  {
    id: "a4",
    firstName: "Four",
    lastName: "Agent",
    title: "Test Test",
    isFeatured: true,
    showOnWebsiteRoster: true,
    showAgentProfilePage: true,
  },
];

const initialTeam = [
  { id: "b1", firstName: "Five", lastName: "Agent", title: "Test Test", isFeatured: false },
  { id: "b2", firstName: "Six", lastName: "Agent", title: "Test Test", isFeatured: false },
  { id: "b3", firstName: "Seven", lastName: "Agent", title: "Test Test", isFeatured: false },
  { id: "b4", firstName: "Eight", lastName: "Agent", title: "Test Test", isFeatured: false },
  { id: "b5", firstName: "Nine", lastName: "Agent", title: "Test Test", isFeatured: false },
  { id: "b6", firstName: "Ten", lastName: "Agent", title: "Test Test", isFeatured: false },
];

const Roster = () => {
  const dispatch = useDispatch();
  const featureFlags = useFeatureFlags();
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showNoSeatsModal, setShowNoSeatsModal] = useState(false);
  const [personDetailsModal, setPersonDetailsModal] = useState({ open: false, personId: null });
  const [preferredLender, setPreferredLender] = useState(null);
  const [showDeleteLenderModal, setShowDeleteLenderModal] = useState(false);
  const [showDeleteAgentModal, setShowDeleteAgentModal] = useState(false);
  const [sortOption, setSortOption] = useState(SORT_OPTIONS[4]);
  const seatsAvailable = useSeatsAvailable();

  // DnD state
  const [featuredAgents, setFeaturedAgents] = useState(initialFeatured);
  const [teamAgents, setTeamAgents] = useState(initialTeam);
  const [activeId, setActiveId] = useState(null);

  useEffect(() => {
    dispatch(getSeatsAvailable());
  }, []);

  if (!featureFlags.websiteRoster) return <Navigate to="/website" />;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const findAgentById = (id) =>
    featuredAgents.find((a) => a.id === id) || teamAgents.find((a) => a.id === id);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    if (featuredAgents.some((a) => a.id === active.id) && featuredAgents.some((a) => a.id === over.id)) {
      const oldIndex = featuredAgents.findIndex((a) => a.id === active.id);
      const newIndex = featuredAgents.findIndex((a) => a.id === over.id);
      setFeaturedAgents(
        arrayMove(featuredAgents, oldIndex, newIndex).map((a) =>
          a.id === active.id ? { ...a, isFeatured: true } : a,
        ),
      );
      return;
    }
    if (teamAgents.some((a) => a.id === active.id) && teamAgents.some((a) => a.id === over.id)) {
      const oldIndex = teamAgents.findIndex((a) => a.id === active.id);
      const newIndex = teamAgents.findIndex((a) => a.id === over.id);
      setTeamAgents(
        arrayMove(teamAgents, oldIndex, newIndex).map((a) =>
          a.id === active.id ? { ...a, isFeatured: false } : a,
        ),
      );
      return;
    }

    if (featuredAgents.some((a) => a.id === active.id) && teamAgents.some((a) => a.id === over.id)) {
      const agent = featuredAgents.find((a) => a.id === active.id);
      setFeaturedAgents(featuredAgents.filter((a) => a.id !== active.id));
      setTeamAgents([...teamAgents, { ...agent, isFeatured: false }]);
      return;
    }
    if (teamAgents.some((a) => a.id === active.id) && featuredAgents.some((a) => a.id === over.id)) {
      if (featuredAgents.length >= 4) return;
      const agent = teamAgents.find((a) => a.id === active.id);
      setTeamAgents(teamAgents.filter((a) => a.id !== active.id));
      setFeaturedAgents([...featuredAgents, { ...agent, isFeatured: true }]);
    }
  };

  return (
    <div className="tw-flex-1 tw-max-w-[1382px] tw-mx-auto tw-flex tw-flex-col tw-gap-[16px] tw-p-[16px]">
      <div className="tw-relative tw-flex tw-flex-col tw-gap-[32px] tw-bg-white tw-border-[1px] tw-border-solid tw-border-gray-10 tw-rounded-[8px] tw-p-[32px] tw-overflow-auto">
        <WebsiteHeader
          title="Roster"
          description={
            <>
              Showcase your team on your Brivity website by adding agents, setting a preferred lender,
              customizing display order, and featuring key team members. <TextLink>Learn more</TextLink>.
            </>
          }
          actions={
            <>
              <TextComboInput
                containerClassName="tw-flex-1 tw-max-w-[280px] tw-w-[280px] tw-min-w-[200px]"
                placeholder="Search agents by name"
                trailing={<MagnifyingGlassSolidV6 size="l" className="tw-text-gray-30" />}
              />
              <Button
                size="medium"
                schema="tertiary"
                className="tw-whitespace-nowrap tw-flex tw-items-center tw-gap-[8px]"
              >
                View Team Page <ArrowUpRightFromSquareSolidV6 />
              </Button>
              <Popover placement="bottom-end">
                <Popover.Trigger>
                  <Button size="medium" className="tw-whitespace-nowrap tw-flex tw-items-center tw-gap-[8px]">
                    <PlusSolidV6 /> Add To Roster
                  </Button>
                </Popover.Trigger>
                <Popover.Content>
                  {ADD_OPTIONS.map((option) => (
                    <Popover.Item
                      key={option.value}
                      className="!tw-h-auto"
                      onClick={() => {
                        if (option.value === "website_profile") {
                          setShowAboutModal(true);
                        } else if (seatsAvailable) {
                          setPersonDetailsModal({ open: true, personId: null });
                        } else {
                          setShowNoSeatsModal(true);
                        }
                      }}
                    >
                      <div className="tw-flex tw-flex-col tw-gap-[4px] tw-max-w-[244px]">
                        {option.label}
                        <span className="tw-text-gray-40 tw-text-14d">{option.meta.description}</span>
                      </div>
                    </Popover.Item>
                  ))}
                </Popover.Content>
              </Popover>
            </>
          }
        />
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={(event) => setActiveId(event.active.id)}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveId(null)}
      >
        <div className="tw-flex tw-gap-[16px] tw-flex-wrap">
          <div className="tw-flex-1">
            <CollapsibleWebsiteCard
              title={`Featured Agents (${featuredAgents.length}/4)`}
              action={
                <span className="tw-text-12d tw-text-gray-40">
                  Featured agents appear larger and higher on the page than the rest of the team
                </span>
              }
            >
              <SortableContext items={featuredAgents.map((a) => a.id)} strategy={rectSortingStrategy}>
                <div className="tw-flex tw-flex-wrap tw-gap-[24px]">
                  {featuredAgents.map((agent) => (
                    <SortableAgentLenderCard
                      key={agent.id}
                      id={agent.id}
                      agent={agent}
                      onStar={() => {
                        setFeaturedAgents(featuredAgents.filter((a) => a.id !== agent.id));
                        setTeamAgents([...teamAgents, { ...agent, isFeatured: false }]);
                      }}
                      popoverOptions={[
                        <PopoverItem key="website">Visit Profile Page</PopoverItem>,
                        <PopoverItem
                          key="delete"
                          className="tw-text-semantic-red-100"
                          onClick={() => setShowDeleteAgentModal(true)}
                        >
                          Delete Website Agent
                        </PopoverItem>,
                      ]}
                    />
                  ))}
                  {/* Show placeholder if dragging from teamAgents */}
                  {activeId &&
                    !featuredAgents.some((a) => a.id === activeId) &&
                    featuredAgents.length < 4 && (
                      <AgentLenderCard agent={findAgentById(activeId)} className="tw-opacity-50" />
                    )}
                </div>
              </SortableContext>
            </CollapsibleWebsiteCard>
          </div>
          <CollapsibleWebsiteCard
            className="tw-basis-[300px]"
            panelClassName="tw-flex-1"
            title="Preferred Lender"
            allowCollapse={false}
            action={
              <Button size="small" onClick={() => setPreferredLender({})}>
                + Add
              </Button>
            }
          >
            <AgentLenderCard
              className="tw-max-w-full"
              lender={{ firstName: "Test", lastName: "Lender", title: "Test Test" }}
              onEdit={() => setPreferredLender({ id: 1, firstName: "Test", lastName: "Lender" })}
              popoverOptions={[
                <PopoverItem key="website">Visit Website</PopoverItem>,
                <PopoverItem key="copy">Copy Website Link</PopoverItem>,
                <PopoverItem
                  key="delete"
                  className="tw-text-semantic-red-100"
                  onClick={() => setShowDeleteLenderModal(true)}
                >
                  Delete Lender
                </PopoverItem>,
              ]}
            />
          </CollapsibleWebsiteCard>
        </div>
        <div className="tw-flex tw-gap-[16px] tw-flex-wrap">
          <CollapsibleWebsiteCard
            className="tw-flex-1"
            title="Our Team"
            action={
              <div className="tw-flex tw-items-center tw-gap-[4px]">
                <span className="tw-text-12d tw-text-gray-40">Sort on website by</span>
                <Popover>
                  <Popover.Trigger>
                    <Button
                      size="small"
                      schema="secondary"
                      className="!tw-min-w-[140px] tw-flex tw-items-center"
                    >
                      <span className="tw-flex-1 tw-text-center">{sortOption.label}</span>
                      <ChevronDownSolidV6 className="tw-justify-self-end" size="s" />
                    </Button>
                  </Popover.Trigger>
                  <Popover.Content>
                    {SORT_OPTIONS.map((option) => (
                      <Popover.Item key={option.value} onClick={() => setSortOption(option)}>
                        {option.label}
                      </Popover.Item>
                    ))}
                  </Popover.Content>
                </Popover>
              </div>
            }
            allowCollapse={false}
          >
            <SortableContext items={teamAgents.map((a) => a.id)} strategy={rectSortingStrategy}>
              <div className="tw-flex tw-flex-wrap tw-gap-[24px] min-h-[120px]">
                {teamAgents.map((agent) => (
                  <SortableAgentLenderCard
                    key={agent.id}
                    id={agent.id}
                    agent={agent}
                    starDisabled={featuredAgents.length >= 4}
                    onStar={() => {
                      setTeamAgents(teamAgents.filter((a) => a.id !== agent.id));
                      setFeaturedAgents([...featuredAgents, { ...agent, isFeatured: true }]);
                    }}
                    popoverOptions={[
                      <PopoverItem key="website">Visit Profile Page</PopoverItem>,
                      <PopoverItem
                        key="delete"
                        className="tw-text-semantic-red-100"
                        onClick={() => setShowDeleteAgentModal(true)}
                      >
                        Delete Website Agent
                      </PopoverItem>,
                    ]}
                  />
                ))}
                {/* Show placeholder if dragging from featuredAgents */}
                {activeId && !teamAgents.some((a) => a.id === activeId) && (
                  <AgentLenderCard agent={findAgentById(activeId)} className="tw-opacity-50" />
                )}
              </div>
            </SortableContext>
          </CollapsibleWebsiteCard>
          <div className="tw-basis-[300px]" />
        </div>
        <DragOverlay>
          {activeId ? (
            <div style={{ minWidth: 200, minHeight: 120 }}>
              <AgentLenderCard agent={findAgentById(activeId)} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      <AboutWebsiteProfilesModal show={showAboutModal} onClose={() => setShowAboutModal(false)} />

      <NoSeatsRemainingModal show={showNoSeatsModal} onClose={() => setShowNoSeatsModal(false)} />

      <PersonDetailsModal
        modalType="user"
        show={personDetailsModal.open}
        personId={personDetailsModal.personId}
        onClose={(person) => {
          console.log(person);
          setPersonDetailsModal({ open: false, personId: null });
        }}
      />

      <PreferredLenderModal
        open={Boolean(preferredLender)}
        preferredLender={preferredLender}
        onClose={() => setPreferredLender(null)}
        onDelete={() => {
          setPreferredLender(null);
          setShowDeleteLenderModal(true);
        }}
      />

      <DeleteLenderModal show={showDeleteLenderModal} onClose={() => setShowDeleteLenderModal(false)} />
      <DeleteAgentModal show={showDeleteAgentModal} onClose={() => setShowDeleteAgentModal(false)} />
    </div>
  );
};

export default Roster;
